import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { api } from "@/lib/api";
import ReportBugModal from "@/components/ReportBugModal";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, FolderArchive, BookOpen, User as UserIcon,
  CreditCard, Settings as SettingsIcon, LogOut, Menu, X, Shield, Bug, ChevronDown, Megaphone, Compass, Lock, Users, Code2,
} from "lucide-react";

// Owner-only nav items — executors get a stripped-down nav (no billing,
// settings, brand kit access etc.). Team page is owner-only too.
const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/strategy", label: "90-Day Strategy", icon: Compass, testid: "nav-strategy", premium: true },
  { to: "/tools/html-studio", label: "HTML Studio", icon: Code2, testid: "nav-html-studio", premium: true },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns" },
  { to: "/vault", label: "Content Vault", icon: FolderArchive, testid: "nav-vault" },
  { to: "/downloads", label: "Resources", icon: BookOpen, testid: "nav-resources" },
  { to: "/team", label: "Team", icon: Users, testid: "nav-team", ownerOnly: true },
  { to: "/account", label: "Account", icon: UserIcon, testid: "nav-account", ownerOnly: true },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing", ownerOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

// Executor accounts never see billing/account/team/brand-kit — hide those from the nav.
const executorNavItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/strategy", label: "90-Day Strategy", icon: Compass, testid: "nav-strategy", premium: true },
  { to: "/tools/html-studio", label: "HTML Studio", icon: Code2, testid: "nav-html-studio", premium: true },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns" },
  { to: "/vault", label: "Content Vault", icon: FolderArchive, testid: "nav-vault" },
  { to: "/downloads", label: "Resources", icon: BookOpen, testid: "nav-resources" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { sub, badgeForTier } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);

  useEffect(() => { setDrawer(false); }, [location.pathname]);

  const initials = (user?.name || user?.email || "?")
    .split(/\s+|@/).filter(Boolean).slice(0, 2)
    .map(s => s[0]?.toUpperCase()).join("");

  const handleLogout = async () => { await logout(); navigate("/"); };

  const planLabel = badgeForTier ? badgeForTier(sub?.tier) : "Free trial";

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      <aside className="hidden lg:flex w-[260px] flex-col border-r border-[#E2E8F0] bg-white" data-testid="app-sidebar">
        <Sidebar user={user} sub={sub} onBugOpen={() => setBugOpen(true)} />
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-[#0A2342]/40" onClick={() => setDrawer(false)} />
          <div className="relative w-[280px] max-w-[85vw] bg-white shadow-xl animate-fade-up">
            <Sidebar user={user} sub={sub} onBugOpen={() => setBugOpen(true)} onClose={() => setDrawer(false)} />
          </div>
        </div>
      )}

      {/* Top bar (visible on all sizes) */}
      <div className="lg:hidden fixed inset-x-0 top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0] px-4 py-3 flex items-center justify-between">
        <button data-testid="mobile-menu-open" onClick={() => setDrawer(true)} className="text-[#0A2342]"><Menu className="w-5 h-5" /></button>
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[#0A2342] flex items-center justify-center">
            <span className="text-[#4FD1C5] font-heading font-bold text-xs">D</span>
          </div>
          <span className="font-heading font-bold text-[14px] text-[#0A2342]">DMS</span>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="topbar-user-mobile" className="w-9 h-9 rounded-full bg-[#1D4E89] text-white flex items-center justify-center font-semibold text-sm">
              {initials || "D"}
            </button>
          </DropdownMenuTrigger>
          <UserMenuContent user={user} sub={sub} planLabel={planLabel} navigate={navigate} onBugOpen={() => setBugOpen(true)} onLogout={handleLogout} />
        </DropdownMenu>
      </div>

      <main className="flex-1 min-w-0 mt-14 lg:mt-0">
        {/* Desktop top bar with clickable user avatar */}
        <div className="hidden lg:flex sticky top-0 z-30 h-14 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0] px-6 items-center justify-end gap-4">
          <button
            data-testid="topbar-report-bug"
            onClick={() => setBugOpen(true)}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#64748B] hover:text-[#0A2342] px-3 py-1.5 rounded-md hover:bg-[#F1F5F9]"
          >
            <Bug className="w-3.5 h-3.5" /> Report a bug
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="topbar-user"
                className="flex items-center gap-2.5 px-2 py-1 rounded-md hover:bg-[#F1F5F9]"
              >
                <div className="w-8 h-8 rounded-full bg-[#1D4E89] text-white flex items-center justify-center font-semibold text-[12.5px]">{initials || "D"}</div>
                <div className="text-left leading-tight">
                  <div className="text-[12.5px] font-semibold text-[#0A2342] max-w-[140px] truncate">{user?.name || "Diver"}</div>
                  <div className="text-[10.5px] text-[#64748B]">{planLabel}</div>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-[#64748B]" />
              </button>
            </DropdownMenuTrigger>
            <UserMenuContent user={user} sub={sub} planLabel={planLabel} navigate={navigate} onBugOpen={() => setBugOpen(true)} onLogout={handleLogout} />
          </DropdownMenu>
        </div>

        <Outlet />
        {children}
      </main>

      <ReportBugModal open={bugOpen} onClose={() => setBugOpen(false)} />
    </div>
  );
}

function UserMenuContent({ user, sub, planLabel, navigate, onBugOpen, onLogout }) {
  return (
    <DropdownMenuContent
      align="end"
      sideOffset={8}
      className="w-64 bg-white rounded-lg shadow-xl border border-[#E2E8F0] p-0 z-[60]"
      data-testid="user-menu"
    >
      <DropdownMenuLabel className="px-4 py-3 border-b border-[#F1F5F9] font-normal">
        <div className="text-[13.5px] font-heading font-bold text-[#0A2342] truncate">{user?.name || "Diver"}</div>
        <div className="text-[11.5px] text-[#64748B] truncate">{user?.email}</div>
        <div className="mt-2 inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 rounded-full bg-[#0A2342] text-[#4FD1C5]">
          {planLabel}{sub?.status === "trialing" && sub?.trial_days_left ? ` · ${sub.trial_days_left}d trial` : ""}
        </div>
      </DropdownMenuLabel>
      {[
        { to: "/account", label: "Account", icon: UserIcon, testid: "user-menu-account" },
        { to: "/billing", label: "Billing", icon: CreditCard, testid: "user-menu-billing" },
        { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "user-menu-settings" },
      ].map(({ to, label, icon: Icon, testid }) => (
        <DropdownMenuItem
          key={to}
          onSelect={() => navigate(to)}
          data-testid={testid}
          className="cursor-pointer px-4 py-2 text-[13.5px] text-[#1F2937] focus:bg-[#F1F5F9] focus:text-[#0A2342] rounded-none"
        >
          <Icon className="w-3.5 h-3.5 text-[#64748B]" /> {label}
        </DropdownMenuItem>
      ))}
      <DropdownMenuItem
        onSelect={onBugOpen}
        data-testid="user-menu-report-bug"
        className="cursor-pointer px-4 py-2 text-[13.5px] text-[#1F2937] focus:bg-[#F1F5F9] rounded-none"
      >
        <Bug className="w-3.5 h-3.5 text-[#64748B]" /> Report a bug
      </DropdownMenuItem>
      {user?.is_admin && (
        <DropdownMenuItem
          onSelect={() => navigate("/admin")}
          data-testid="user-menu-admin"
          className="cursor-pointer px-4 py-2 text-[13.5px] text-[#0A2342] font-semibold focus:bg-[#F1F5F9] rounded-none"
        >
          <Shield className="w-3.5 h-3.5 text-[#4FD1C5]" /> Admin
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator className="my-1" />
      <DropdownMenuItem
        onSelect={onLogout}
        data-testid="user-menu-logout"
        className="cursor-pointer px-4 py-2 text-[13.5px] text-[#B91C1C] focus:bg-[#FEF2F2] focus:text-[#B91C1C] rounded-none"
      >
        <LogOut className="w-3.5 h-3.5" /> Sign out
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

function Sidebar({ user, sub, onBugOpen, onClose }) {
  const [strategyAccess, setStrategyAccess] = useState(null);
  const [roleCtx, setRoleCtx] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/strategy/access");
        if (!cancelled) setStrategyAccess(data);
      } catch { /* not logged in yet */ }
      try {
        const { data } = await api.get("/team/me");
        if (!cancelled) setRoleCtx(data);
      } catch { /* not logged in yet */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const isExecutor = roleCtx?.is_executor === true;
  const items = isExecutor ? executorNavItems : navItems;
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="px-6 py-5 border-b border-[#E2E8F0] flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="sidebar-logo">
          <div className="w-8 h-8 rounded-md bg-[#0A2342] flex items-center justify-center">
            <span className="text-[#4FD1C5] font-heading font-bold text-sm">D</span>
          </div>
          <div>
            <div className="font-heading font-bold text-[15px] text-[#0A2342] leading-none">Dive Media</div>
            <div className="text-[11px] tracking-[0.22em] text-[#64748B] uppercase mt-1">Strategist</div>
          </div>
        </Link>
        {onClose && (
          <button onClick={onClose} data-testid="sidebar-close" className="text-[#64748B]"><X className="w-5 h-5" /></button>
        )}
      </div>
      {isExecutor && (
        <div data-testid="sidebar-executor-badge" className="px-4 pt-3 pb-1">
          <div className="rounded-md border border-[#4FD1C5]/40 bg-[#F0FDFA] px-3 py-2 text-[11.5px] text-[#0F766E]">
            <div className="font-bold uppercase tracking-[0.18em] text-[10.5px]">Executor seat</div>
            <div className="mt-0.5 text-[#0A2342] font-semibold truncate" title={roleCtx?.owner_name}>
              {roleCtx?.owner_name ? `${roleCtx.owner_name}'s workspace` : "Team workspace"}
            </div>
          </div>
        </div>
      )}
      {/* Workspace switcher is visible for BOTH owners and executors — an
          executor can be a member of multiple workspaces (invited by
          different owners) and needs to switch between them from a single
          login. The switcher itself renders member workspaces with an
          "Executor" pill + owner-name subtitle. */}
      <div className="px-3 pt-3">
        <WorkspaceSwitcher />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, testid, premium }) => {
          const locked = premium && strategyAccess && !strategyAccess.has_access;
          return (
            <NavLink
              key={to} to={to} data-testid={testid}
              onClick={() => onClose?.()}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-[14.5px] transition-all ${
                  isActive ? "bg-[#0A2342] text-white" : "text-[#1F2937] hover:bg-[#F1F5F9]"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.75} />
              <span className="font-medium flex-1">{label}</span>
              {locked && <Lock className="w-3 h-3 text-[#94A3B8]" data-testid={`${testid}-lock`} />}
              {premium && !locked && <span className="text-[9.5px] font-bold tracking-[0.15em] uppercase text-[#4FD1C5]">Pro</span>}
            </NavLink>
          );
        })}
        {user?.is_admin && (
          <NavLink to="/admin" data-testid="nav-admin" onClick={() => onClose?.()} className={({ isActive }) => `mt-3 flex items-center gap-3 px-3 py-2.5 rounded-md text-[14.5px] transition-all border ${isActive ? "bg-[#0A2342] text-white border-[#0A2342]" : "border-[#4FD1C5]/40 text-[#0A2342] hover:bg-[#4FD1C5]/10"}`}>
            <Shield className="w-4 h-4" /><span className="font-semibold">Admin</span>
          </NavLink>
        )}
      </nav>
      {sub && sub.status !== "archived" && (sub.credit_cap !== null || sub.is_unlimited_credits) && (
        <div className="px-4 py-3 mx-3 mb-3 rounded-md bg-[#F8FAFC] border border-[#E2E8F0]" data-testid="sidebar-usage">
          <div className="flex items-center justify-between text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B]">
            <span>Strategy Credits</span>
            {sub.is_unlimited_credits ? (
              <span data-testid="sidebar-credits-count" className="text-[#0F766E]">Unlimited</span>
            ) : (
              <span data-testid="sidebar-credits-count">{sub.credits_used || 0} / {(sub.credit_cap || 0) + (sub.bonus_credits || 0)}</span>
            )}
          </div>
          {!sub.is_unlimited_credits && (
            <div className="mt-2 h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
              <div className="h-full bg-[#4FD1C5]" style={{ width: `${Math.min(100, ((sub.credits_used || 0) / Math.max(1, (sub.credit_cap || 0) + (sub.bonus_credits || 0))) * 100)}%` }} />
            </div>
          )}
        </div>
      )}
      <div className="px-3 py-3 border-t border-[#E2E8F0]">
        <button
          onClick={onBugOpen}
          data-testid="sidebar-report-bug"
          className="w-full flex items-center gap-2 px-2 py-2 text-[12.5px] text-[#64748B] hover:text-[#0A2342] hover:bg-[#F1F5F9] rounded-md"
        >
          <Bug className="w-3.5 h-3.5" /> Report a bug
        </button>
      </div>
    </div>
  );
}
